import type { EncounterModel } from '@ctw/shared/api/fhir/models/encounter';
import { Button } from '@ctw/shared/components/button';
import { ContentCard } from '@ctw/shared/components/containers/content-card';
import { DefinitionList } from '@ctw/shared/components/definition-list';
import { HorizontalDivider } from '@ctw/shared/components/horizontal-divider';
import { ContentTypeIcon } from '@ctw/shared/components/icons/content-type-icon';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { Tooltip } from '@ctw/shared/components/tooltip';
import { ResponsiveSourceDocumentLink } from '@ctw/shared/content/CCDA/responsive-source-document-link';
import { ReactHtmlRenderer } from '@ctw/shared/content/document-viewer/_internal/renderers/react/react-html-renderer';
import { RelatedDocumentsList } from '@ctw/shared/content/documents/related-documents-list';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { usePatientQuery } from '@ctw/shared/context/patient-provider';
import type { UseResourceDrawerOpenDrawerArgs } from '@ctw/shared/context/resource-drawer-context';
import { useResourceModal } from '@ctw/shared/context/resource-modal-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import type { CdaDocument } from '@ctw/shared/utils/cda/types';
import { getBffApiBaseUrl } from '@ctw/shared/utils/environments';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { faChevronRight, faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { capitalize, isEmpty } from 'lodash-es';
import { useState } from 'react';

export const encounterDrawerAction: UseResourceDrawerOpenDrawerArgs<'Encounter'>['action'] = ({
  model,
}) => {
  const upid = model.patientUPID;
  const shouldShowSourceDocumentLink =
    upid !== undefined && upid !== '' && window.location.pathname.includes(upid);

  return {
    label: 'Encounter',
    title: model.title,
    width: 'wide',
    content: {
      body: (
        <div className={tw`flex flex-col gap-5`}>
          <ContentCard
            variant="focal"
            title="Details"
            content={{
              header: shouldShowSourceDocumentLink && (
                <ResponsiveSourceDocumentLink model={model} />
              ),
            }}
          >
            <DefinitionList
              items={[
                { label: 'Status', value: capitalize(model.status) },
                { label: 'Class', value: capitalize(model.classDisplay) },
                { label: 'Type', value: capitalize(model.typeDisplay) },
                { label: 'Location', value: model.location.join(', ') },
                {
                  label: 'Providers',
                  value: model.practitioners.length > 0 && (
                    <ul className={tw`m-0 list-disc pl-4`}>
                      {model.practitioners.map((p) => (
                        <li key={p}>{p}</li>
                      ))}
                    </ul>
                  ),
                },
                {
                  label: 'Location Type',
                  value: (() => {
                    const locationType = model.locationType.filter(
                      (value) => value.toLowerCase() !== 'unknown',
                    );
                    if (locationType.length > 0) {
                      return (
                        <ul className={tw`m-0 list-disc pl-4`}>
                          {locationType
                            .filter((value) => value.toLowerCase() !== 'unknown')
                            .map((s) => (
                              <li key={s}>{s}</li>
                            ))}
                        </ul>
                      );
                    }
                    return null;
                  })(),
                },
                { label: 'Reason', value: model.reason },
                {
                  label: 'Diagnoses',
                  value: model.diagnoses.length > 0 && (
                    <ul className={tw`m-0 list-disc pl-4`}>
                      {model.diagnoses.map((d) => (
                        <li key={d}>{d}</li>
                      ))}
                    </ul>
                  ),
                },
                { label: 'Discharge Disposition', value: model.dischargeDisposition },
              ]}
            />
          </ContentCard>
          <EncounterDocumentsSection model={model} />
        </div>
      ),
    },
  };
};

interface EncounterDocumentsSectionProps {
  model: EncounterModel;
}

const EncounterDocumentsSection = ({ model }: EncounterDocumentsSectionProps) => {
  const useRelatedBinaries = useFeatureFlag('ctw-encounter-notes-use-related-binaries');

  if (useRelatedBinaries) {
    return <EncounterSourceDocuments model={model} />;
  }

  return (
    <RelatedDocumentsList
      isLoading={model.isLoadingClinicalNotes}
      items={model.docsAndNotes.map((document) => ({ preferred: false, document }))}
    />
  );
};

interface EncounterSourceDocumentsProps {
  model: EncounterModel;
}

const EncounterSourceDocuments = ({ model }: EncounterSourceDocumentsProps) => {
  if (model.binaryIds.length === 0) {
    return null;
  }

  return (
    <div className={tw`space-y-2`}>
      <div className={tw`font-semibold text-lg`}>Source Documents</div>
      <ul className={tw`m-0 list-none space-y-1 p-0`}>
        {model.binaryIds.map((binaryId) => (
          <EncounterSourceDocumentItem key={binaryId} binaryId={binaryId} />
        ))}
      </ul>
    </div>
  );
};

const SOURCE_DOCUMENT_DETAIL_DISPLAY_FIELDS = ['Date', 'Author'] as const;

interface EncounterSourceDocumentItemProps {
  binaryId: string;
}

const EncounterSourceDocumentItem = ({ binaryId }: EncounterSourceDocumentItemProps) => {
  const telemetry = useTelemetry();
  const { openResourceModal } = useResourceModal({
    layout: {
      mode: 'full',
      overflowBehavior: 'hidden',
      fullbleed: true,
      showCloseButton: false,
    },
  });
  const { isLoading, isError, data } = useNarrativeForBinary({ binaryId });
  const canDisplayBinaryLink = !(isLoading || isError) && data !== undefined;
  const hasNarrative = (data?.cda?.content?.sections?.length ?? 0) > 0;
  const displayTitle = data?.title ?? data?.cda?.metadata?.document?.title ?? 'Untitled Document';
  const detailFields = data?.cda?.detailFields
    ?.filter((field) => SOURCE_DOCUMENT_DETAIL_DISPLAY_FIELDS.includes(field.label as never))
    .map((field) => `${field.label}: ${field.value}`)
    .join(' | ');

  const isCollapsible = canDisplayBinaryLink && hasNarrative;
  const [isExpanded, setIsExpanded] = useState(true);
  const showsNarrative = isCollapsible && isExpanded;
  const narrativeRegionId = `source-document-narrative-${binaryId}`;

  return (
    <div className={tw`rounded-md border border-border-main shadow-sm`}>
      <div
        className={twx('flex w-full min-w-full items-center', {
          'rounded-b-none border-border-main border-b': showsNarrative,
        })}
      >
        <Button
          type="button"
          variant="link"
          className={tw`flex flex-1 items-center gap-1 hover:opacity-80`}
          ariaLabel={isExpanded ? 'Collapse document' : 'Expand document'}
          ariaExpanded={isExpanded}
          ariaControls={isCollapsible ? narrativeRegionId : undefined}
          disabled={!isCollapsible}
          onClick={() => {
            setIsExpanded((previous) => !previous);
          }}
        >
          <div className={tw`flex w-full items-center gap-3 px-3 py-4`}>
            {isCollapsible && (
              <FontAwesomeIcon
                icon={faChevronRight}
                className={twx('h-4 w-4 shrink-0 text-content-icon transition-transform', {
                  'rotate-90': isExpanded,
                })}
              />
            )}
            <div className={tw`block whitespace-nowrap text-left`}>
              {isLoading && (
                <div className={tw`flex items-center gap-2 text-content-subtle`}>
                  <LoadingSpinner className={tw`h-5 w-5`} />
                  <div className={tw`font-medium text-[1rem] tracking-wide`}>
                    Loading document...
                  </div>
                </div>
              )}
              {isError && (
                <div className={tw`flex items-center gap-2 text-content-subtle`}>
                  <FontAwesomeIcon icon={faCircleExclamation} className={tw`h-5 w-5`} />
                  <div className={tw`font-medium text-[1rem] tracking-wide `}>
                    Unable to load this document
                  </div>
                </div>
              )}
              {canDisplayBinaryLink && (
                <div className={tw`flex flex-col items-start justify-center`}>
                  <div className={tw`font-medium text-[1rem] text-content-main tracking-wide`}>
                    {displayTitle}
                  </div>
                  {!isEmpty(detailFields) && (
                    <div className={tw`text-content-subtle text-xs leading-tight`}>
                      {detailFields}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </Button>
        <Tooltip
          id="source-document-tooltip"
          content="View source document"
          place="top"
          enabled={canDisplayBinaryLink}
        >
          <Button
            type="button"
            variant="unstyled"
            className={tw`flex shrink-0 items-center justify-center px-4 text-primary-text hover:opacity-80`}
            ariaLabel="View source document"
            disabled={!canDisplayBinaryLink}
            telemetryContext={{ resourceType: 'Binary', resourceId: binaryId }}
            onClick={() =>
              openResourceModal({ telemetry, resourceType: 'Binary', resourceId: binaryId })
            }
          >
            <ContentTypeIcon contentType={data?.contentType ?? ''} className={tw`h-5 w-5`} />
          </Button>
        </Tooltip>
      </div>
      {showsNarrative && (
        <div id={narrativeRegionId} className={tw`flex flex-col gap-1 px-2 py-3`}>
          {data.cda?.content?.sections.map((section, index) => (
            <div key={section.id} className={tw`flex flex-col gap-1`}>
              {index !== 0 && <HorizontalDivider />}
              <div className={tw`font-medium text-[0.9rem]`}>{section.title}</div>
              <div className={tw`text-sm`}>
                <ReactHtmlRenderer
                  id={section.id}
                  parsedHtmlNodes={section.parsedHtmlNodes ?? []}
                  emptyState={undefined}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface BinaryNarrativesResponse {
  title?: string;
  contentType: string;
  cda?: CdaDocument;
}

interface UseBinaryNarrativeArgs {
  binaryId: string;
}

const useNarrativeForBinary = ({ binaryId }: UseBinaryNarrativeArgs) =>
  usePatientQuery({
    enabled: binaryId !== '',
    queryId: 'binary-narrative',
    queryKey: [binaryId],
    queryFn: async ({ requestContext, telemetry }): Promise<BinaryNarrativesResponse> => {
      const response = await fetch(`${getBffApiBaseUrl(requestContext.env)}/v1/binary/narratives`, {
        headers: {
          Authorization: `Bearer ${requestContext.authToken}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        method: 'POST',
        body: JSON.stringify({ binaryId }),
      });

      const failWith = (detail: string): never => {
        const error = new Error(
          `Failed to fetch binary narratives: ${response.status} ${response.statusText} — ${detail}`,
        );
        telemetry.trackError({
          message: 'Failed to fetch binary narratives server-side',
          error,
          context: { binaryId },
        });
        throw error;
      };

      if (!response.ok) {
        const body = await response.text().catch(() => '');
        return failWith(body.slice(0, 500) || 'no response body');
      }

      const responseJson = await response.json().catch((error: unknown) => {
        const message = error instanceof Error ? error.message : String(error);
        return failWith(`response was not valid JSON: ${message}`);
      });

      if (!responseJson?.data?.attributes) {
        return failWith('response was missing data.attributes');
      }

      return responseJson.data.attributes;
    },
  });
